/**
 * Email Signup Popup — 2-Step Marketing Funnel
 *
 * Step 1: Warm-up (shared mobile + desktop)
 * Step 2: Klaviyo newsletter signup
 *
 * Triggers: session timer, scroll depth, exit intent, page count, badge click
 * Suppression: localStorage (submitted), sessionStorage (dismissed/shown)
 */

class EmailSignupPopup extends HTMLElement {
  constructor() {
    super();

    this.SESSION_START_KEY = 'emailPopupSessionStart';
    this.PAGE_COUNT_KEY = 'emailPopupPageCount';
    this.SHOWN_KEY = 'emailPopupShown';
    this.DISMISSED_KEY = 'emailPopupDismissed';
    this.SUBMITTED_KEY = 'emailPopupSubmitted';

    this.modalActive = false;
    this.currentStep = 0;
    this.triggersEnabled = true;
    this.autoTriggerFired = false;
    this.pageLoadTime = Date.now();
  }

  connectedCallback() {
    // Submitted users never see the popup again (persists across sessions)
    if (localStorage.getItem(this.SUBMITTED_KEY) === 'true') {
      this.remove();
      return;
    }

    // Track session start time
    if (!sessionStorage.getItem(this.SESSION_START_KEY)) {
      sessionStorage.setItem(this.SESSION_START_KEY, String(Date.now()));
    }

    // Track page count (only once per element instance to prevent inflation on reconnect)
    if (!this._pageCounted) {
      this._pageCounted = true;
      const pageCount = parseInt(sessionStorage.getItem(this.PAGE_COUNT_KEY) || '0', 10) + 1;
      sessionStorage.setItem(this.PAGE_COUNT_KEY, String(pageCount));
      this.pageCount = pageCount;
    }

    // If already shown or dismissed in this session, disable auto-triggers
    if (
      sessionStorage.getItem(this.SHOWN_KEY) === 'true' ||
      sessionStorage.getItem(this.DISMISSED_KEY) === 'true'
    ) {
      this.triggersEnabled = false;
    }

    this.initElements();
    this.attachEventListeners();
    this.setupBadgeVisibility();

    if (this.triggersEnabled) {
      this.setupAutomaticTriggers();
    }
  }

  disconnectedCallback() {
    this.cleanupTriggers();
    if (this._badgeScrollHandler) window.removeEventListener('scroll', this._badgeScrollHandler);
    if (this._stickyObserver) this._stickyObserver.disconnect();
    if (this._menuObserver) this._menuObserver.disconnect();
    if (this._cartObserver) this._cartObserver.disconnect();
  }

  // ───────── DOM references ─────────

  initElements() {
    this.badge = this.querySelector('.email-popup__badge');
    this.modal = this.querySelector('.email-popup__modal');
    this.overlay = this.querySelector('[data-email-popup-overlay]');
    this.closeBtn = this.querySelector('[data-email-popup-close]');
    this.form = this.querySelector('[data-email-popup-form]');
    this.errorDiv = this.querySelector('[data-email-popup-error]');
    this.successDiv = this.querySelector('[data-email-popup-success]');
    this.steps = this.querySelectorAll('.email-popup__step');
  }

  // ───────── Events ─────────

  attachEventListeners() {
    this.badge?.addEventListener('click', () => this.openModal());
    this.closeBtn?.addEventListener('click', () => this.dismiss());
    this.overlay?.addEventListener('click', () => this.dismiss());

    this._onKeydown = (e) => {
      if (e.key === 'Escape' && this.modalActive) this.dismiss();
    };
    document.addEventListener('keydown', this._onKeydown);

    // Step 1 CTAs
    this.querySelectorAll('[data-action="go-step-2"]').forEach((btn) => {
      btn.addEventListener('click', () => this.goToStep(2));
    });

    // Dismiss CTAs
    this.querySelectorAll('[data-action="dismiss"]').forEach((btn) => {
      btn.addEventListener('click', () => this.dismiss());
    });

    // Newsletter form submit
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleFormSubmit();
    });
  }

  // ───────── Badge visibility ─────────

  setupBadgeVisibility() {
    this._badgeHiddenByDrawer = false;
    this._badgeHiddenByTS = false;
    this._lastScrollY = window.scrollY;
    this._isMobile = window.innerWidth < 750;

    // --- Mobile: mirror Trusted Shops widget behavior (hides on scroll down) ---
    if (this._isMobile) {
      // Initially assume TS widget is visible (user at top), so hide badge
      this._badgeHiddenByTS = window.scrollY < 50;
      this._badgeScrollHandler = () => {
        const currentY = window.scrollY;
        const delta = currentY - this._lastScrollY;
        if (Math.abs(delta) < 5) return; // ignore tiny scrolls
        // Scrolling down → TS hides → show badge
        // Scrolling up → TS shows → hide badge
        this._badgeHiddenByTS = delta < 0 || currentY < 50;
        this._lastScrollY = currentY;
        this.updateBadgeVisibility();
      };
      window.addEventListener('scroll', this._badgeScrollHandler, { passive: true });
    }

    // --- Sticky buy button: raise badge when visible (mobile only) ---
    const stickyWrapper = document.getElementById('stickyWrapper');
    if (stickyWrapper && this._isMobile) {
      this._stickyObserver = new MutationObserver(() => {
        const isVisible = !stickyWrapper.classList.contains('hide');
        this.badge.classList.toggle('email-popup__badge--raised', isVisible);
      });
      this._stickyObserver.observe(stickyWrapper, { attributes: true, attributeFilter: ['class'] });
      // Set initial state
      this.badge.classList.toggle('email-popup__badge--raised', !stickyWrapper.classList.contains('hide'));
    }

    // --- Menu drawer: hide badge when open ---
    const menuDrawer = document.querySelector('.menu-drawer-container');
    if (menuDrawer) {
      this._menuObserver = new MutationObserver(() => {
        this._badgeHiddenByDrawer = menuDrawer.classList.contains('menu-opening');
        this.updateBadgeVisibility();
      });
      this._menuObserver.observe(menuDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    // --- Cart drawer: hide badge when open ---
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      this._cartObserver = new MutationObserver(() => {
        this._badgeHiddenByDrawer = cartDrawer.classList.contains('active');
        this.updateBadgeVisibility();
      });
      this._cartObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    this.updateBadgeVisibility();
  }

  updateBadgeVisibility() {
    if (this.modalActive || localStorage.getItem(this.SUBMITTED_KEY) === 'true') return;
    const shouldHide = this._badgeHiddenByDrawer || (this._isMobile && this._badgeHiddenByTS);
    this.badge.hidden = shouldHide;
  }

  // ───────── Trigger system ─────────

  setupAutomaticTriggers() {
    const timeDelay = parseInt(this.dataset.timeDelay, 10) || 30;
    const pageChangeDelay = parseInt(this.dataset.pageChangeDelay, 10) || 10;
    const scrollDepth = parseInt(this.dataset.scrollDepth, 10) || 70;
    const exitIntent = this.dataset.exitIntent === 'true';
    const pageCountThreshold = parseInt(this.dataset.pageCountThreshold, 10) || 2;

    // Gate: don't auto-trigger until enough pages visited
    if (this.pageCount < pageCountThreshold) return;

    // Calculate how long until time-based trigger should fire
    const sessionStart = parseInt(sessionStorage.getItem(this.SESSION_START_KEY), 10);
    const elapsed = (Date.now() - sessionStart) / 1000;
    const remainingDelay = Math.max(0, timeDelay - elapsed);
    // Never fire immediately on a new page — wait at least pageChangeDelay
    const effectiveDelay = Math.max(pageChangeDelay, remainingDelay);

    // Time-based trigger
    this._timeTimer = setTimeout(() => {
      this.autoOpen();
    }, effectiveDelay * 1000);

    // Scroll depth trigger (throttled)
    if (scrollDepth > 0) {
      let scrollTicking = false;
      this._scrollHandler = () => {
        if (scrollTicking || this.autoTriggerFired) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
          scrollTicking = false;
          if (this.autoTriggerFired || this.modalActive) return;

          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          // Skip short pages (less than 1.5× viewport)
          if (scrollable < window.innerHeight * 0.5) return;

          const pct = (window.scrollY / scrollable) * 100;
          if (pct >= scrollDepth) {
            this.autoOpen();
          }
        });
      };
      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    // Exit intent (desktop only)
    if (exitIntent && window.innerWidth >= 750) {
      this._exitHandler = (e) => {
        if (this.autoTriggerFired || this.modalActive) return;
        if (e.clientY <= 0) {
          this.autoOpen();
        }
      };
      document.addEventListener('mouseout', this._exitHandler);
    }
  }

  autoOpen() {
    if (this.autoTriggerFired || this.modalActive || !this.triggersEnabled) return;

    const minPageTime = (parseInt(this.dataset.pageChangeDelay, 10) || 10) * 1000;
    const elapsed = Date.now() - this.pageLoadTime;
    if (elapsed < minPageTime) {
      if (!this._deferTimer) {
        this._deferTimer = setTimeout(() => {
          this._deferTimer = null;
          this.autoOpen();
        }, minPageTime - elapsed);
      }
      return;
    }

    this.autoTriggerFired = true;
    this.cleanupTriggers();
    this.openModal();
  }

  cleanupTriggers() {
    if (this._timeTimer) clearTimeout(this._timeTimer);
    if (this._deferTimer) clearTimeout(this._deferTimer);
    if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
    if (this._exitHandler) document.removeEventListener('mouseout', this._exitHandler);
  }

  // ───────── Modal control ─────────

  openModal() {
    this.modalActive = true;
    this.goToStep(1);
    this.modal.setAttribute('open', '');
    document.body.classList.add('email-popup-open');
    this.badge.hidden = true;

    sessionStorage.setItem(this.SHOWN_KEY, 'true');

    // Trap focus (Dawn helper)
    if (typeof trapFocus === 'function') {
      setTimeout(() => trapFocus(this.modal, this.modal.querySelector('.email-popup__container')), 100);
    }
  }

  closeModal() {
    this.modalActive = false;
    this.modal.removeAttribute('open');
    document.body.classList.remove('email-popup-open');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }

    // Re-evaluate badge visibility (respects drawers, TS widget, etc.)
    this.updateBadgeVisibility();
  }

  dismiss() {
    sessionStorage.setItem(this.DISMISSED_KEY, 'true');
    this.triggersEnabled = false;
    this.cleanupTriggers();
    this.closeModal();
  }

  // ───────── Step navigation ─────────

  goToStep(step) {
    this.currentStep = step;
    this.steps.forEach((el) => {
      el.classList.toggle('active', parseInt(el.dataset.step, 10) === step);
    });

    // Update aria-labelledby to current heading
    const heading = this.modal.querySelector('.email-popup__step.active .email-popup__heading');
    if (heading) {
      heading.id = 'email-popup-heading';
      this.modal.setAttribute('aria-labelledby', 'email-popup-heading');
    }

    // Auto-focus email input on Step 2
    if (step === 2) {
      const input = this.form?.querySelector('input[type="email"]');
      if (input) setTimeout(() => input.focus(), 150);
    }
  }

  // ───────── Form submission (Klaviyo) ─────────

  async handleFormSubmit() {
    const input = this.form.querySelector('input[type="email"]');
    const email = input?.value?.trim();
    const submitBtn = this.form.querySelector('.email-popup__cta-primary');

    // Clear error
    this.errorDiv.classList.remove('visible');
    this.errorDiv.textContent = '';
    input.classList.remove('has-error');

    // Validate
    if (!email || !this.isValidEmail(email)) {
      input.classList.add('has-error');
      this.errorDiv.textContent = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
      this.errorDiv.classList.add('visible');
      input.focus();
      return;
    }

    // Loading state
    submitBtn.disabled = true;
    submitBtn.classList.add('loading');

    try {
      await this.submitToKlaviyo(email);
      this.markSubmitted();

      // Show success, hide form
      this.form.style.display = 'none';
      const privacyNotice = this.querySelector('.email-popup__step.active .email-popup__privacy-notice');
      if (privacyNotice) privacyNotice.style.display = 'none';
      this.successDiv.classList.add('visible');

      // Auto-close after delay
      setTimeout(() => this.closeModal(), 2500);
    } catch (err) {
      console.error('Klaviyo signup error:', err);
      this.errorDiv.textContent = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
      this.errorDiv.classList.add('visible');
      submitBtn.disabled = false;
      submitBtn.classList.remove('loading');
    }
  }

  async submitToKlaviyo(email) {
    const listId = this.dataset.klaviyoListId;
    const apiKey = this.dataset.klaviyoApiKey;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email }
            }
          },
          custom_source: 'Email Popup'
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId }
          }
        }
      }
    };

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(apiKey)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          revision: '2024-07-15'
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      throw new Error(`Klaviyo API error: ${response.status}`);
    }
  }

  // ───────── Helpers ─────────

  markSubmitted() {
    localStorage.setItem(this.SUBMITTED_KEY, 'true');
    this.badge.hidden = true;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}

customElements.define('email-signup-popup', EmailSignupPopup);
